import { cellCenter } from '../grid.ts';
import { iatan2, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, GearboxRules, GearboxState, Hero, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { BOX_IN, boxRight, boxTrain } from './cable-layout.ts';
import { gargAt, gargFlock, gargTick } from './garg.ts';
import { shootTick } from './workshop.ts';

/**
 * The gearbox (docs/sendas/artifice.md, chapter 2, act 3, the test; §4, puzzle 2, «La caja de cambios»; the ground and
 * the rule in battle/cable-layout.ts). The car hangs below the winch house with Tormo's arms in it; the winch that
 * hauls it in has its gearbox open. The windmill turns axle A at 60; B is two gears on two shafts chained together (B1
 * meshes with A, B2 with C: they turn as one); C drives the drum; R, the loose axle, goes in between B2 and C and only
 * turns the way round once more. The drum must turn at what its dial asks and the way of the cable's arrow:
 * 60 × A ÷ B1 × B2 ÷ C. The gears lie on a shelf, their teeth painted on them.
 *
 * A tap on a gear or a slot sends the hero: standing still `stand` by it (3 s), what is in its hands and what is there
 * change places (`boxOrder`); carrying one slows it (`carryBp`). The clutch lever (`pull`) puts the winch to the drum:
 * right, the cable sings and the car comes in; too fast, the car slams into the pylon (the platform shakes: `slamBp`
 * of the hero's life, Dunna's blows); too slow or the wrong way, it hangs out there longer and more gargoyles come
 * (`flock`). A wrong pull lets the clutch go again: nothing else is undone.
 *
 * Gargoyles come in flocks onto the box (battle/garg.ts): one sitting on a slot or on the lever won't let it be worked
 * till it is struck off. La Picotera, a great gargoyle, sits on the drum the whole fight: every `peck.every` she
 * pecks, warned 3 s (she draws her head back over the spot): at the hero if it is within her `reach`, else at a gear in
 * the box within it, which she knocks out back onto its shelf (staying away to strike her from afar costs the train). Her
 * wing-beat (`beat`, warned 3 s, the wings spread) strikes everything on the platform, wherever it stands: her fight
 * can't be long. She calls two gargoyles every `call.every`, and more once at half her life. Won with the train right
 * and La Picotera down (either first); lost with the hero or Dunna down.
 *
 * Cost: the train is worked out only when a gear is put, taken or knocked out (five slots); the flyers'.
 */

/** The clutch lever's mark (after the slots and the gears' lying spots). */
export const boxLever = (r: GearboxRules): number => 5 + r.pieces.length;

const fresh = (r: GearboxRules): GearboxState => ({
  on: r.pieces.map((_, k) => 5 + k),
  hand: -1,
  job: -1,
  held: 0,
  errors: 0,
  last: 0,
  lastAt: -1,
  right: -1,
  haul: -1,
  flock: r.flock.first,
  boss: 0,
  peck: r.boss.peck.every,
  peckAt: { x: 0, y: 0 },
  peckSlot: -2,
  beat: r.boss.beat.first,
  beatAt: -1,
  call: r.boss.call.every,
  half: false,
  down: -1,
  shot: 0,
});

/** A mark's cell: a slot (0–4), a gear's lying spot (5 + its index), the lever. */
export const boxCell = (r: GearboxRules, at: number): number => (at < 5 ? (r.slots[at] as number) : at < 5 + r.pieces.length ? (r.lie[at - 5] as number) : r.lever);

/** The gear at a mark (−1: none). */
export const boxAt = (w: GearboxState, at: number): number => w.on.indexOf(at);

/** The teeth in each slot (A, B1, B2, C, R; 0 empty). */
export const boxSet = (r: GearboxRules, w: GearboxState): number[] => {
  const set = [0, 0, 0, 0, 0];
  w.on.forEach((at, k) => {
    if (at >= 0 && at < 5) set[at] = r.pieces[k] as number;
  });
  return set;
};

/** The hero's pace with a gear in its hands (SENDA.gearbox.carryBp by its teeth: 12, 18, 24, 36). */
export const boxCarryBp = (s: BattleState): number => {
  const r = s.rules?.senda?.gearbox;
  const w = s.senda?.gearbox;
  if (!r || !w || w.hand < 0) return 10000;
  const t = r.pieces[w.hand] as number;
  return r.carryBp[t >= 36 ? 3 : t >= 24 ? 2 : t >= 18 ? 1 : 0] as number;
};

/** The hero sent to a mark (a tap on it): a gear, a slot, the lever. */
export const boxOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.gearbox;
  const w = s.senda?.gearbox;
  if (!r || !w || w.right >= 0 || at < 0 || at > boxLever(r)) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, boxCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** A slot's perch on the box (the gargoyles' list: slots then the lever, by `slot`): who sits on it. */
const sitting = (s: BattleState, at: number): boolean => {
  const r = s.rules?.senda?.garg;
  if (!r) return false;
  const slot = at < 5 ? at : at === boxLever(s.rules?.senda?.gearbox as GearboxRules) ? 5 : -1;
  if (slot < 0) return false;
  const i = r.perches.findIndex((p) => p.kind === 3 && p.slot === slot);
  return i >= 0 && !!gargAt(s, i);
};

/** The clutch pulled: the drum put to the train. */
const pull = (s: BattleState, r: GearboxRules, w: GearboxState, cue: (c: string) => void): void => {
  const set = boxSet(r, w);
  const t = boxTrain(set);
  const lc = cellCenter(s.map.radius, r.lever);
  if (!t.full) {
    ev(s, { t: 'senda', kind: 'clutch', npc: '', x: lc.x, y: lc.y, n: 4 });
    cue('loose');
    return;
  }
  if (boxRight(set, r.want, r.way)) {
    w.right = s.tick;
    ev(s, { t: 'senda', kind: 'clutch', npc: '', x: lc.x, y: lc.y, n: 0 });
    cue('right');
    // What it had in its hands it puts down (the train is done).
    w.hand = -1;
    return;
  }
  const [a = 0, b1 = 0, b2 = 0, c = 0] = set;
  const way = (t.dir[3] as number) === r.way;
  const fast = BOX_IN * a * b2 > r.want * b1 * c;
  const kind = !way ? 3 : fast ? 1 : 2;
  w.errors++;
  w.last = kind;
  w.lastAt = s.tick;
  ev(s, { t: 'senda', kind: 'clutch', npc: '', x: lc.x, y: lc.y, n: kind });
  cue(`err${kind}`);
  cue(`err${w.errors}`);
  if (kind === 1) {
    // The car slams into the pylon: the platform shakes under everyone.
    for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.err.slamBp), null, 'physical', true);
    const d = s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0);
    if (d) {
      d.hp = d.hp > r.err.blows ? d.hp - r.err.blows : 0;
      ev(s, { t: 'senda', kind: d.hp > 0 ? 'hit' : 'down', npc: d.id, x: d.x, y: d.y });
    }
    ev(s, { t: 'senda', kind: 'tilt', npc: '', x: lc.x, y: lc.y, n: 1 });
  } else gargFlock(s, r.err.flock, 0, [3], 10000);
};

/** The hero's hands at the mark: standing still by it, what is in them and what is there change places (or the lever is pulled). */
const heroWork = (s: BattleState, r: GearboxRules, w: GearboxState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, boxCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  // A gargoyle sitting on it: nothing to be done there till it is struck off.
  if (sitting(s, w.job)) {
    w.held = 0;
    cue('sat');
    return;
  }
  const lever = w.job === boxLever(r);
  if (++w.held < (lever ? r.pull : r.stand)) return;
  w.held = 0;
  if (lever) {
    w.job = -1;
    pull(s, r, w, cue);
    return;
  }
  const at = w.job;
  const there = boxAt(w, at);
  const hand = w.hand;
  if (there < 0 && hand < 0) {
    w.job = -1;
    return;
  }
  if (there >= 0) {
    w.on[there] = -1;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: c.x, y: c.y, n: at });
  }
  if (hand >= 0) {
    w.on[hand] = at;
    ev(s, { t: 'senda', kind: 'put', npc: '', x: c.x, y: c.y, n: at });
    if (at < 5) cue('put');
  }
  w.hand = there;
  w.job = -1;
  if (at < 5) {
    const t = boxTrain(boxSet(r, w));
    if (t.full) {
      const dc = cellCenter(s.map.radius, r.slots[3] as number);
      ev(s, { t: 'senda', kind: 'turn', npc: '', x: dc.x, y: dc.y, n: (t.dir[3] as number) * (t.speed[3] as number) });
    }
  }
};

/** La Picotera: perched on the drum, her pecks, her wing-beat, her calls. */
const bossTick = (s: BattleState, r: GearboxRules, w: GearboxState, cue: (c: string) => void): void => {
  const B = r.boss;
  const e = w.boss ? s.enemies.find((x) => x.id === w.boss) : undefined;
  if (w.down >= 0) return;
  if (!e || e.hp <= 0) {
    w.down = s.tick;
    ev(s, { t: 'senda', kind: 'pickDown', npc: '', x: B.at.x, y: B.at.y });
    cue('bossDown');
    return;
  }
  e.x = B.at.x;
  e.y = B.at.y;
  if (e.anim === 'attack' && e.animT > 8) e.anim = 'idle';
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The peck: warned over its spot, then struck.
  if (w.peckSlot === -2 && s.tick >= w.peck) {
    if (h && withinRange(h.x, h.y, B.at.x, B.at.y, B.peck.reach)) {
      w.peckSlot = -1;
      w.peckAt = { x: h.x, y: h.y };
    } else if (w.right < 0) {
      // Out of her reach: a gear of the box within it, knocked out (the nearest to her).
      let best = -1;
      let bd = 0x7fffffff;
      for (let at = 0; at < 5; at++) {
        if (boxAt(w, at) < 0) continue;
        const c = cellCenter(s.map.radius, r.slots[at] as number);
        if (!withinRange(c.x, c.y, B.at.x, B.at.y, B.peck.reach)) continue;
        const dd = Math.abs(c.x - B.at.x) + Math.abs(c.y - B.at.y);
        if (dd < bd) {
          bd = dd;
          best = at;
        }
      }
      if (best >= 0) {
        w.peckSlot = best;
        w.peckAt = cellCenter(s.map.radius, r.slots[best] as number);
      }
    }
    if (w.peckSlot === -2) w.peck = s.tick + 12;
    else {
      w.peck = s.tick + B.peck.warn;
      e.facing = iatan2(w.peckAt.y - e.y, w.peckAt.x - e.x);
      ev(s, { t: 'senda', kind: 'peckWarn', npc: '', x: w.peckAt.x, y: w.peckAt.y, n: w.peckSlot });
      cue('peck');
    }
  } else if (w.peckSlot >= -1 && s.tick >= w.peck) {
    e.anim = 'attack';
    e.animT = 0;
    ev(s, { t: 'senda', kind: 'peck', npc: '', x: w.peckAt.x, y: w.peckAt.y, n: w.peckSlot });
    if (w.peckSlot < 0) {
      for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, w.peckAt.x, w.peckAt.y, B.peck.r)) damageHero(s, x, mulBp(x.stats.maxHp, B.peck.heroBp), e, 'physical', true);
    } else if (w.right < 0) {
      const k = boxAt(w, w.peckSlot);
      if (k >= 0) {
        w.on[k] = 5 + k;
        ev(s, { t: 'senda', kind: 'knockOut', npc: '', x: w.peckAt.x, y: w.peckAt.y, n: w.peckSlot });
        cue('knock');
        if (w.job === w.peckSlot) w.held = 0;
      }
    }
    w.peckSlot = -2;
    w.peck = s.tick + B.peck.every - B.peck.warn;
  }
  // The wing-beat: nothing on the platform is out of it.
  if (w.beatAt < 0 && s.waveT >= w.beat) {
    w.beatAt = s.tick + B.beat.warn;
    ev(s, { t: 'senda', kind: 'beatWarn', npc: '', x: B.at.x, y: B.at.y, n: B.beat.warn });
    cue('beat');
  } else if (w.beatAt >= 0 && s.tick >= w.beatAt) {
    w.beatAt = -1;
    w.beat = s.waveT + B.beat.every - B.beat.warn;
    ev(s, { t: 'senda', kind: 'beat', npc: '', x: B.at.x, y: B.at.y });
    for (const x of s.heroes) if (x.downTicks === 0) damageHero(s, x, mulBp(x.stats.maxHp, B.beat.heroBp), e, 'physical', true);
    const d = s.senda?.npcs.find((n) => n.id === 'dunna' && n.hp > 0);
    if (d) {
      d.hp = d.hp > B.beat.blows ? d.hp - B.beat.blows : 0;
      ev(s, { t: 'senda', kind: d.hp > 0 ? 'hit' : 'down', npc: d.id, x: d.x, y: d.y });
    }
  }
  // Her call: two off the abyss; at half her life, more, once.
  if (s.waveT >= w.call) {
    w.call = s.waveT + B.call.every;
    gargFlock(s, B.call.n, 0, [3], 10000);
    ev(s, { t: 'senda', kind: 'call', npc: '', x: B.at.x, y: B.at.y, n: B.call.n });
    cue('call');
  }
  if (!w.half && e.hp * 2 <= e.maxHp) {
    w.half = true;
    gargFlock(s, B.call.half, 0, [3], 10000);
    ev(s, { t: 'senda', kind: 'call', npc: '', x: B.at.x, y: B.at.y, n: B.call.half });
    cue('half');
  }
};

export const gearboxTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.gearbox;
  const st = s.senda;
  if (!r || !st) return;
  if (!st.gearbox) {
    st.gearbox = fresh(r);
    st.garg = { list: [] };
    const e = spawnEnemy(s, 'gargoyle', 3, false, 0, r.boss.at.x, r.boss.at.y, r.boss.hp);
    e.facing = 512;
    e.anim = 'idle';
    st.gearbox.boss = e.id;
    st.garg.list.push({ id: e.id, perch: -1, mode: 4, cd: 0, crow: false });
  }
  const w = st.gearbox;
  if (!s.started) return;
  gargTick(s);
  heroWork(s, r, w, cue);
  bossTick(s, r, w, cue);
  if (s.waveT >= w.flock) {
    w.flock = s.waveT + r.flock.every;
    gargFlock(s, r.flock.n, 0, [3], r.flock.perchBp);
    cue('flock');
  }
  if (w.right >= 0 && w.haul < 0 && s.tick >= w.right + r.haul) {
    w.haul = s.tick;
    ev(s, { t: 'senda', kind: 'belt', npc: '', x: r.boss.at.x, y: r.boss.at.y });
    cue('haul');
  }
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** The train right, La Picotera down: two steps. */
export const gearboxGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.gearbox;
  if (!w) return { done: 0, need: 2 };
  return { done: (w.right >= 0 ? 1 : 0) + (w.down >= 0 ? 1 : 0), need: 2 };
};

/** Won with the car hauled in and La Picotera down (a moment after). */
export const gearboxResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.gearbox;
  if (!w || w.haul < 0 || w.down < 0) return null;
  return s.tick >= (w.haul > w.down ? w.haul : w.down) + 24 ? 'won' : null;
};
